import { useEffect, useState } from "react";

import useSKU from "../../hooks/useSKU";
import useSurvey from "../../hooks/useSurvey";
import useLocalization from "../../hooks/useLocalization";

import { Box, Typography } from "@mui/material";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";

import Card from "../layout/Card";
import CheckBoxGroup from "../elements/CheckBoxGroup";
import Switch from "../elements/Switch";
import InfoBox from "../layout/InfoBox";
import EditorToolbar from "./EditorToolbar";
import Select from "../elements/Select";

const SkuMatrixEditor = ({ surveyId, skuMatrix, onSave }) => {
  const { getSurvey } = useSurvey();
  const { getSkus, skus, skusLoaded, customFields } = useSKU();
  const { getTitle, getLabel } = useLocalization();

  const [survey, setSurvey] = useState(null);
  const [show, setShow] = useState(skuMatrix?.show ? skuMatrix.show : null);
  const [applyListings, setapplyListings] = useState(
    skuMatrix?.applyListings ? true : false,
  );
  const [items, setItems] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState([]);

  const fields = [
    { id: "upc", name: getLabel("upc") },
    { id: "clientCode", name: getLabel("clientCode") },
    { id: "packSize", name: getLabel("packSize") },
    { id: "image", name: "Image" },
  ];

  useEffect(() => {
    if (!skuMatrix) return;

    setItems(() => {
      if (Array.isArray(skuMatrix.items) && skuMatrix.items.length > 0) {
        let newItems = skuMatrix.items.map((item) => item.skuId);
        return newItems;
      }

      return [];
    });
  }, [skuMatrix]);

  useEffect(() => {
    getData();
  }, [surveyId]);

  const getData = async () => {
    const surveyData = await getSurvey(surveyId);
    setSurvey(surveyData);

    await getSkus({ clientId: surveyData.clientId });
  };

  const handleShowFields = (newValue) => {
    setShow((current) => {
      if (current) {
        return { ...current, fields: newValue };
      }

      return { fields: newValue };
    });
  };

  const handleShowCustom = (newValue) => {
    setShow((current) => {
      if (current) {
        return { ...current, custom: newValue };
      }

      return { custom: newValue };
    });
  };

  const handleSave = async () => {
    onSave({
      show,
      applyListings,
      items: items.map((item) => {
        return { skuId: item };
      }),
    });
  };

  const categoryOptions = Array.isArray(skus)
    ? Array.from(
        new Set(
          skus
            .map((sku) => sku.category)
            .filter((category) => category && category.trim() !== ""),
        ),
      ).map((category) => ({ id: category, name: category }))
    : [];

  const filteredSkus = Array.isArray(skus)
    ? categoryFilter.length > 0
      ? skus.filter((sku) => categoryFilter.includes(sku.category))
      : skus
    : [];

  const visibleSkuIds = filteredSkus.map((sku) => sku.id);
  const allVisibleSelected =
    visibleSkuIds.length > 0 && visibleSkuIds.every((id) => items.includes(id));

  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      setItems((current) =>
        current.filter((id) => !visibleSkuIds.includes(id)),
      );
      return;
    }

    setItems((current) => Array.from(new Set([...current, ...visibleSkuIds])));
  };

  return (
    <Box>
      <EditorToolbar onSave={handleSave} />
      <Box
        sx={{ padding: "24px", display: "flex" }}
        gap={2}
      >
        <Card
          title={getTitle("options")}
          sx={{ flex: 1 }}
        >
          <Box sx={{ padding: "8px" }}>
            <CheckBoxGroup
              label={getLabel("showFields")}
              options={fields}
              value={show?.fields || []}
              sx={{ backgroundColor: "#eee" }}
              onChange={handleShowFields}
            />
          </Box>
          <Box sx={{ padding: "8px" }}>
            <CheckBoxGroup
              label={getLabel("showClientFields")}
              options={customFields.map((field) => {
                return { id: field, name: field };
              })}
              value={show?.custom || []}
              sx={{ backgroundColor: "#eee" }}
              onChange={handleShowCustom}
            />
          </Box>
          <Box sx={{ padding: "8px" }}>
            <InfoBox
              label={getLabel("applyListings")}
              sx={{ backgroundColor: "#eee" }}
            >
              <Switch
                value={applyListings}
                onChange={(newValue) => setapplyListings(newValue)}
                label={getLabel("apply")}
              />
            </InfoBox>
          </Box>
        </Card>

        <Card
          title={getTitle("skus")}
          sx={{ flex: 3 }}
        >
          <Box sx={{ padding: "8px" }}>
            {Array.isArray(skus) && skus.length > 0 ? (
              <>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "16px",
                  }}
                  gap={1}
                >
                  <Box
                    sx={{ display: "flex", alignItems: "center" }}
                    onClick={toggleSelectAllVisible}
                  >
                    {allVisibleSelected ? (
                      <CheckBoxIcon color="primary" />
                    ) : (
                      <CheckBoxOutlineBlankIcon color="primary" />
                    )}
                    <Typography sx={{ fontSize: "12px" }}>
                      Select All
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Select
                      id="skuCategoryFilter"
                      label="category"
                      value={categoryFilter}
                      onChange={(newValue) => setCategoryFilter(newValue)}
                      items={categoryOptions}
                      multiSelect
                    />
                  </Box>
                </Box>
                <CheckBoxGroup
                  options={filteredSkus}
                  value={items}
                  onChange={(newValue) => setItems(newValue)}
                  label={getLabel("selectSkusForMatrix")}
                  sx={{ backgroundColor: "#eee" }}
                />
              </>
            ) : (
              <Typography>
                You need to import SKU's for the client first
              </Typography>
            )}
          </Box>
        </Card>
      </Box>
    </Box>
  );
};

export default SkuMatrixEditor;
